import React, { useCallback, useState, useEffect } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import Progress from '../Form/Progress'
import { useLocalReturnDataState } from '../../../stores/LocalReturnDataProvider'
import { ButtonWithLoadingState } from '../../Buttons'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { emptyGuid } from '../../../utils/emptyGuid'
import ArraySelect from '../../Select/ArraySelect'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import MandatoryField from '../../MandatoryField/MandatoryField'
import ArticleScan from '../../LocalReceipts/Form/ArticleScan'
import { BiBarcodeReader } from 'react-icons/bi'

const StyledContainer = styled.div`
  @media screen and (min-width: 1081px) {
    position: sticky;
    top: 50px;
  }
`
const StyledCancelButton = styled(Button)`
  width: 100%;
  max-width: 250px;
  @media (max-width: 567px) {
    max-width: none;
  }
`
const StyledScanButton = styled(Button)`
  width: 100%;
  max-width: 250px;
`

const StoreReturnSidebar = () => {
  const {
    submitEditReturn,
    submitStoreReturn,
    errorsOccurredOnSubmit,
    formDataHasChanged,
    resetFormData,
    setSuccessSubmit,
    successSubmit,
    formErrors,
    resetErrorByFormKey,
    loadedReturnData,
    storehouse,
    setStorehouse,
    headerData, addScannedArticle
  } = useLocalReturnDataState()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [availableStorehouses, setAvailableStorehouses] = useState([])
  const [openScanModal, setOpenScanModal,] = useState(false)
  const disabled = headerData.status !== 'open'
  useEffect(() => {
    //load settings data
    ; (async () => {
      const storehouses = await apiClient.getJson(APIEndpoints.storehouses())
      setAvailableStorehouses(storehouses.filter((item) => item.isVirtual))
    })()
  }, [])
  const handleSubmit = useCallback(
    async (e) => {
      setSuccessSubmit(false)
      setIsLoading(true)
      formDataHasChanged && (await submitEditReturn())
      setIsLoading(false)
    },
    [formDataHasChanged, setSuccessSubmit, submitEditReturn]
  )

  const handleSubmitIsStored = useCallback(
    async (e) => {
      setSuccessSubmit(false)
      setIsSaving(true)
      !formDataHasChanged && (await submitStoreReturn())
      setIsSaving(false)
    },
    [formDataHasChanged, setSuccessSubmit, submitStoreReturn]
  )
  const handleCange = (e) => {
    const { name, value } = e.target
    setStorehouse({ id: value })
    resetErrorByFormKey(name)
  }
  const handleClickCancel = () => {
    resetFormData()
  }
  useEffect(() => {
    let timeout = null
    if (!isLoading && !!successSubmit) {
      timeout = setTimeout(() => {
        setSuccessSubmit(false)
      }, 3000)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [isLoading, successSubmit, setSuccessSubmit])

  const { t } = useTranslation()
  return (
    <StyledContainer>
      <header>
        <div className='h2'>
          {t('returnsReceiptOverlayProgress:headerStore')}
        </div>
      </header>
      <div className='content'>
        <StyledScanButton className="mb-3"
          onClick={() => setOpenScanModal(true)}
          disabled={isLoading || !loadedReturnData}
        >
          <BiBarcodeReader className='me-3' />
          {t('productsOverview:articleScan')}
        </StyledScanButton>
        <Form.Group className='mb-3'>
          <Form.Label className='d-block'>
            {t('returnsTable:warehouse')} *
          </Form.Label>
          <ArraySelect
            returnEvent
            options={availableStorehouses}
            defaultOption={{ name: t('pleaseSelect'), value: emptyGuid }}
            name='storehouse.id'
            value={storehouse.id}
            onChange={handleCange}
            isInvalid={!!formErrors['storehouseid']}
            disabled={disabled}
          />
          <Form.Control.Feedback type='invalid'>
            {formErrors['storehouseid']}
          </Form.Control.Feedback>
          <MandatoryField />
        </Form.Group>
        <ButtonWithLoadingState
          className='submit-btn mb-3'
          isLoading={isLoading}
          onClick={handleSubmit}
          disabled={
            isLoading || !formDataHasChanged || !loadedReturnData || disabled
          }
          buttonText={t('OverlayProgress:save')}
          loadingText={t('OverlayProgress:save')}
        />
        {formDataHasChanged && (
          <>
            <StyledCancelButton
              className='mb-3'
              variant='secondary'
              onClick={handleClickCancel}
            >
              {t('cancelButton')}
            </StyledCancelButton>
            <div className=' alert alert-info'>
              {t('EmployeeFormHealthData:dataChange')}
            </div>
          </>
        )}
        <Progress isStoring={true} />
        <div className='mb-5' />
        <ButtonWithLoadingState
          className='submit-btn mb-3'
          disabled={
            isLoading ||
            !!formDataHasChanged ||
            isSaving ||
            !loadedReturnData ||
            disabled
          }
          buttonText={t('returnsReceiptOverlayProgress:store')}
          loadingText={t('returnsReceiptOverlayProgress:store')}
          isLoading={isSaving}
          onClick={handleSubmitIsStored}
        />
        {!isLoading && !isSaving && !!errorsOccurredOnSubmit && (
          <Alert variant='danger'>
            <Trans i18nKey='returnsReceiptOverlayProgress:danger' />
          </Alert>
        )}
        {!isLoading && !isSaving && !!successSubmit && (
          <Alert variant='success'>
            {t('returnsReceiptOverlayProgress:updated')}
          </Alert>
        )}
      </div>
      {openScanModal && (
        <ArticleScan
          handleClose={() => setOpenScanModal(false)}
          addScannedArticle={addScannedArticle}
        />
      )}
    </StyledContainer>
  )
}

export default StoreReturnSidebar
